import React from 'react';
import { Card, SectionHeader } from '@/components/Card';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { CountryPhoneInput } from '@/components/CountryPhoneInput';
import { TemplateButton, ButtonType } from '@/services/templates.service';
import { findCountryByCode } from '@/data/countries';
import { Trash2, Phone, Link as LinkIcon, Reply, KeySquare, GripVertical, Plus, MessageCircleReply } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  value: TemplateButton[];
  onChange: (next: TemplateButton[]) => void;
  /** Meta rejects COPY_CODE buttons on UTILITY templates (subcode 2388180) —
      pass the template category so the option is disabled there. */
  category?: string;
}

const TYPE_META: Record<ButtonType, { label: string; icon: React.ComponentType<{ size?: number }>; help: string }> = {
  URL:         { label: 'URL',          icon: LinkIcon, help: 'Opens a website or app link.' },
  PHONE:       { label: 'Call',         icon: Phone,    help: 'Dials the given number.' },
  QUICK_REPLY: { label: 'Quick reply',  icon: Reply,    help: 'Sends a one-tap reply back to your bot.' },
  COPY_CODE:   { label: 'Copy code',    icon: KeySquare, help: 'Copies a code (e.g. coupon, OTP) to clipboard.' },
};

export const ButtonsEditor: React.FC<Props> = ({ value, onChange, category }) => {
  const copyCodeBlocked = category === 'utility';
  const update = (i: number, patch: Partial<TemplateButton>) =>
    onChange(value.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = (type: ButtonType) => onChange([...value, { type, text: '', ...(type === 'URL' ? { url: '' } : type === 'PHONE' ? { phone: '', countryCode: 'AE' } : type === 'COPY_CODE' ? { url: '' } : {}) }]);

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <SectionHeader
          title="Buttons"
          subtitle="Optional. Up to 10 buttons. Mix URL, Call, Copy code, and Quick reply."
          icon={<MessageCircleReply size={15} />}
          className="mb-0"
        />
        <div className="flex items-center gap-1">
          {(Object.keys(TYPE_META) as ButtonType[]).map((t) => {
            const Icon = TYPE_META[t].icon;
            const blocked = t === 'COPY_CODE' && copyCodeBlocked;
            return (
              <button
                key={t}
                type="button"
                onClick={() => add(t)}
                disabled={value.length >= 10 || blocked}
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-text-secondary transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                title={blocked
                  ? 'Meta does not allow Copy code buttons on Utility templates — use Marketing (coupon) or Authentication (OTP) instead.'
                  : `Add ${TYPE_META[t].label} button`}
              >
                <Plus size={12} /> <Icon size={12} /> {TYPE_META[t].label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-5 py-4 space-y-3">
        {value.length === 0 && (
          <div className="rounded-lg border border-dashed border-border bg-surface-alt/50 px-4 py-6 text-center">
            <div className="text-sm font-medium text-text-secondary">No buttons yet</div>
            <div className="mt-0.5 text-xs text-text-muted">Add a quick reply, link, call, or copy-code button using the controls above.</div>
          </div>
        )}

        {value.map((b, i) => {
          const Icon = TYPE_META[b.type].icon;
          return (
            <div key={i} className="rounded-xl border border-border bg-surface-alt/40 p-3">
              <div className="mb-2.5 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-soft text-primary">
                    <Icon size={14} />
                  </span>
                  <span className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
                    {TYPE_META[b.type].label}
                  </span>
                  <span className="text-2xs text-text-muted">— {TYPE_META[b.type].help}</span>
                </div>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                  aria-label="Remove button"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {/* Button text — every type except COPY_CODE, whose label is
                    Meta-fixed ("Copy code", localized) and not part of the payload. */}
                {b.type !== 'COPY_CODE' ? (
                  <div className="sm:col-span-1">
                    <label className="block text-2xs font-medium text-text-muted mb-1">Button label (shown to recipient)</label>
                    <Input
                      value={b.text}
                      maxLength={25}
                      onChange={(e) => update(i, { text: e.target.value })}
                      placeholder={
                        b.type === 'PHONE' ? 'Call us' :
                        b.type === 'URL' ? 'Visit website' :
                        'Yes, please'
                      }
                    />
                    <div className="mt-1 text-2xs text-text-muted">{b.text.length} / 25</div>
                  </div>
                ) : (
                  <div className="sm:col-span-1">
                    <label className="block text-2xs font-medium text-text-muted mb-1">Button label</label>
                    <div className="flex h-10 items-center rounded-lg border border-border bg-surface-alt/60 px-3 text-sm text-text-muted">
                      "Copy code" — fixed by WhatsApp, shown in the recipient's language
                    </div>
                  </div>
                )}

                {/* Per-type secondary field */}
                {b.type === 'URL' && (
                  <div className="sm:col-span-1">
                    <label className="block text-2xs font-medium text-text-muted mb-1">URL (variables allowed for trailing path)</label>
                    <Input
                      value={b.url ?? ''}
                      onChange={(e) => update(i, { url: e.target.value })}
                      placeholder="https://example.com/{{path}}"
                      iconLeft={<LinkIcon size={14} />}
                    />
                  </div>
                )}
                {b.type === 'PHONE' && (
                  <div className="sm:col-span-1">
                    <label className="block text-2xs font-medium text-text-muted mb-1">Phone number</label>
                    <CountryPhoneInput
                      countryCode={b.countryCode ?? 'AE'}
                      number={extractLocalNumber(b.phone, b.countryCode)}
                      onChange={({ countryCode, e164 }) => update(i, { countryCode, phone: e164 })}
                      placeholder="58 174 2300"
                    />
                  </div>
                )}
                {b.type === 'QUICK_REPLY' && (
                  <div className="sm:col-span-1">
                    <label className="block text-2xs font-medium text-text-muted mb-1">Payload (optional, sent to your webhook)</label>
                    <Input
                      value={b.payload ?? ''}
                      onChange={(e) => update(i, { payload: e.target.value })}
                      placeholder="confirm_yes"
                    />
                    <div className="mt-1 text-2xs text-text-muted">If left blank, the recipient's tap sends back the button label as the message.</div>
                  </div>
                )}
                {b.type === 'COPY_CODE' && (
                  <div className="sm:col-span-1">
                    <label className="block text-2xs font-medium text-text-muted mb-1">Code to copy (placeholder allowed: {`{{code}}`})</label>
                    <Input
                      value={b.url ?? ''}
                      onChange={(e) => update(i, { url: e.target.value })}
                      placeholder="{{code}}"
                      iconLeft={<KeySquare size={14} />}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {value.length > 0 && (
          <div className={clsx('text-2xs text-text-muted text-right', value.length === 10 && 'text-warning')}>
            {value.length} / 10 buttons
          </div>
        )}
      </div>
    </Card>
  );
};

/** Drop the dial code prefix from an E.164 string so the local number can be edited cleanly. */
function extractLocalNumber(e164: string | undefined, countryCode: string | undefined): string {
  if (!e164) return '';
  const country = findCountryByCode(countryCode);
  if (country && e164.startsWith(country.dial)) return e164.slice(country.dial.length);
  if (e164.startsWith('+')) return e164.replace(/^\+\d+/, '');
  return e164;
}
